/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ApiStatus } from '../../../ts';
import { FeatureFlag } from '../../../ts/feature-flags';
import { ELASTIC_SEARCH_AUTH_TYPES, Language } from '../../../ts/skill';
import type { RootState } from '../../../ts/state';
import {
  CONFIDENCE_THRESHOLD_VALUE,
  RESPONSE_LENGTH_VALUE
} from '../../discovery/util/constants';
import { selectAssistantLanguage } from '../../ia/store/assistants/core-selectors';
import { isFeatureEnabled } from '../../ia/store/features/selectors';

/**
 * Selector to test whether Indices are loading
 * @param {Object} state The current state
 * @return {boolean} true if the Indexs are loading, otherwise false
 */
export const selectAreIndicesLoading = (state: RootState) => {
  return state.elastic?.indicesStatus === ApiStatus.LOADING;
};
export const selectAreIndicesStatus = (state: RootState) => {
  return state.elastic?.indicesStatus;
};

export const selectIndicesError = (state: RootState) => {
  return state.elastic?.indicesError;
};

export const selectIndices = (state: RootState) => {
  return state.elastic?.indices;
};

export const isShowDetailConfig = (state: RootState) => {
  return state.elastic?.showDetailConfig ?? false;
};

export const selectIsDirty = (state: RootState) => state.elastic?.isDirty;

export const selectIsEditing = (state: RootState) => state.elastic?.isEditing;

export const selectDraftSkillId = (state: RootState) =>
  state.elastic?.draft?.skill_id;

export const selectDraftSearchSkill = (state: RootState) =>
  state.elastic?.draft;

// NOTE a pre-created search skill shell has empty search_settings
export const selectIsDraftUninitialized = (state: RootState) =>
  !state.elastic?.draft?.search_settings?.schema_mapping;

export const selectDraftSuccessMessage = (state: RootState) =>
  state.elastic?.draft?.search_settings?.messages?.success ?? '';

export const selectDraftNoResultsMessage = (state: RootState) =>
  state.elastic?.draft?.search_settings?.messages?.no_result ?? '';

export const selectDraftConnectivityMessage = (state: RootState) =>
  state.elastic?.draft?.search_settings?.messages?.error ?? '';

export const selectDraftTitleOptionValue = (state: RootState) =>
  state.elastic?.draft?.search_settings?.schema_mapping?.title ?? '';

export const selectDraftBodyOptionValue = (state: RootState) =>
  state.elastic?.draft?.search_settings?.schema_mapping?.body ?? '';

export const selectDraftUrlOptionValue = (state: RootState) =>
  state.elastic?.draft?.search_settings?.schema_mapping?.url ?? '';

export const selectDraftElasticFilterValue = (state: RootState) =>
  state.elastic?.draft?.search_settings?.elastic_search?.filter;

export const selectDraftElasticQueryBodyValue = (state: RootState) =>
  state.elastic?.draft?.search_settings?.elastic_search?.query_body;

export const selectDraftCitationsTitleValue = (state: RootState) =>
  state.elastic?.draft?.search_settings?.conversational_search
    ?.citations_title ?? '';

export const selectDraftMaxCitationsValue = (state: RootState) =>
  state.elastic?.draft?.search_settings?.conversational_search?.citations
    ?.max_citations ?? -1;

export const selectDraftIsConversationalSearchEnabled = (state: RootState) =>
  state.elastic?.draft?.search_settings?.conversational_search?.enabled ??
  false;

export const selectDraftConversationalSearchMultiTurn = (state: RootState) =>
  state.elastic?.draft?.search_settings?.conversational_search?.multi_turn
    ?.enabled ?? false;

export const selectDraftConversationalSearchRetrievalConfidenceThreshold = (
  state: RootState
) =>
  state.elastic?.draft?.search_settings?.conversational_search
    ?.search_confidence?.pre_gen_threshold ??
  CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchConfidenceThreshold = (
  state: RootState
) =>
  state.elastic?.draft?.search_settings?.conversational_search
    ?.search_confidence?.threshold ?? CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchVerbosityOption = (
  state: RootState
) =>
  state.elastic?.draft?.search_settings?.conversational_search?.response_length
    ?.option ?? RESPONSE_LENGTH_VALUE.MODERATE;

export const selectDraftConversationalSearchResponseConfidenceThreshold = (
  state: RootState
) =>
  state.elastic?.draft?.search_settings?.conversational_search
    ?.search_confidence?.post_gen_threshold ??
  CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftElasticUrl = (state: RootState) =>
  state.elastic?.draft?.search_settings?.elastic_search?.url ?? '';

export const selectDraftElasticPort = (state: RootState) =>
  state.elastic?.draft?.search_settings?.elastic_search?.port ?? '';

export const selectDraftElasticUser = (state: RootState) => {
  return state.elastic?.draft?.search_settings?.elastic_search?.username ?? '';
};
export const selectDraftElasticPass = (state: RootState) =>
  state.elastic?.draft?.search_settings?.elastic_search?.password ?? '';

export const selectDraftElasticApiKey = (state: RootState) =>
  state.elastic?.draft?.search_settings?.elastic_search?.apikey ?? '';

export const selectDraftElasticIndex = (state: RootState) =>
  state.elastic?.draft?.search_settings?.elastic_search?.index ?? '';

export const selectDraftManagedIndex = (state: RootState) =>
  state.elastic?.draft?.search_settings?.elastic_search?.managed_index ?? '';

export const selectElasticAuthType = (state: RootState) =>
  state.elastic?.elasticAuthType ?? '';

export const selectPreviouslySavedElasticAuthType = (state: RootState) =>
  state.elastic?.previouslySavedElasticAuthType ?? '';

export const selectPreviouslySelectedIndex = (state: RootState) =>
  state.elastic?.previouslySelectedIndex ?? '';

export const selectPreviouslyManagedIndex = (state: RootState) =>
  state.elastic?.previouslyManagedIndex ?? '';

export const selectElasticIndexInputMethod = (state: RootState) =>
  state.elastic?.elasticIndexInputMethod ?? null;

export const selectIsElserSupported = (state: RootState) =>
  state.elastic?.isElserSupported ?? '';

export const selectElserModelId = (state: RootState) =>
  state.elastic?.elserModelId ?? '';

export const selectContentMapping = (state: RootState) =>
  state.elastic?.contentMapping ?? [];

export const selectContentMappingStatus = (state: RootState) =>
  state.elastic?.contentMappingStatus === ApiStatus.LOADING;

export const selectIsElserSupportedLoading = (state: RootState) => {
  return state.elastic?.isElserSupportedStatus === ApiStatus.LOADING;
};
export const selectIsElserSupportedStatus = (state: RootState) => {
  return state.elastic?.isElserSupportedStatus;
};
export const selectIsCreateElserLoading = (state: RootState) => {
  return state.elastic?.createElserStatus === ApiStatus.LOADING;
};
export const selectCreateElserError = (state: RootState) => {
  return state.elastic?.createElserError;
};

export const selectFiles = (state: RootState) => {
  return state.elastic?.listFiles ?? [];
};
export const selectListFilesLoading = (state: RootState) => {
  return state.elastic?.listFilesStatus === ApiStatus.LOADING;
};
export const selectUploadFilesLoading = (state: RootState) => {
  return state.elastic?.uploadFilesStatus === ApiStatus.LOADING;
};
export const selectDeleteFilesLoading = (state: RootState) => {
  return state.elastic?.deleteFilesStatus === ApiStatus.LOADING;
};
export const selectDeleteFilesSucceeded = (state: RootState) => {
  return state.elastic?.deleteFilesStatus === ApiStatus.SUCCEEDED;
};

const selectIsSapConversationalSearchEnabled = (state: RootState) =>
  isFeatureEnabled(state, FeatureFlag.SAP_CONVERSATIONAL_SEARCH);

export const selectIsDocumentUploadEnabled = (state: RootState) => {
  const assistantLanguage = selectAssistantLanguage(state);

  return (
    assistantLanguage === Language.English &&
    isFeatureEnabled(state, FeatureFlag.ELASTIC_SEARCH_DOCUMENT_UPLOAD)
  );
};

// Will be set to false if there are any validation errors when configuring the elastic setup.
export const selectIsElasticSetupValid = (state: RootState) =>
  state.elastic?.isElasticSetupValid ?? false;

// Will be set to false if there are any validation errors when configuring the elastic setup or if any of the values
// are missing from the setup. Only the auth fields relevant to the current auth type are required for the setup to be
// valid. Password and api key are not required to be resubmitted when editing as long as their relevant auth type was
// the one previously saved for the skill. There is no check for a port field because that field is optional.
export const selectIsSetupValid = (state: RootState) => {
  // Start by checking if there are any validation errors in the elastic setup.
  let validSetup = !!selectIsElasticSetupValid(state);

  // Then, if the the ElasticUrl don't exist the setup is invalid.
  if (!selectDraftElasticUrl(state)) {
    validSetup = false;
  }

  // Next check for different fields depending on the auth type.
  if (selectElasticAuthType(state) === ELASTIC_SEARCH_AUTH_TYPES.BASIC) {
    // If the auth type is set to basic then an ElasticUser is required.
    if (!selectDraftElasticUser(state)) {
      validSetup = false;
    }

    // If the auth type is set to basic then an ElasticPass is required.
    if (!selectDraftElasticPass(state)) {
      // However, if the user is editing an existing Elasticsearch setup, and the previously used ElasticAuthType is
      // also basic, then a password is not required.
      if (
        !selectIsEditing(state) ||
        selectPreviouslySavedElasticAuthType(state) !==
          ELASTIC_SEARCH_AUTH_TYPES.BASIC
      ) {
        // So if there's no ElasticPass and the user is not editing, or they are editing but the
        // previouslySavedElasticAuthType is not basic then the setup isn't valid.
        validSetup = false;
      }
    }
  } else if (selectElasticAuthType(state) === ELASTIC_SEARCH_AUTH_TYPES.API) {
    // If the auth type is set to api then an ElasticApiKey is required.
    if (!selectDraftElasticApiKey(state)) {
      // However, if the user is editing an existing Elasticsearch setup, and the previously used ElasticAuthType is
      // also api, then an api key is not required.
      if (
        !selectIsEditing(state) ||
        selectPreviouslySavedElasticAuthType(state) !==
          ELASTIC_SEARCH_AUTH_TYPES.API
      ) {
        // So if there's no ElasticApiKey and the user is not editing, or they are editing but the
        // previouslySavedElasticAuthType is not api then the setup isn't valid.
        validSetup = false;
      }
    }
  }

  return validSetup;
};

// Will be set to false if there are any validation errors when configuring the elastic config.
export const selectIsElasticConfigValid = (state: RootState) =>
  state.elastic?.isElasticConfigValid ?? false;

// If Sap conversational search is enabled then there will not be a citations title value. However, if Sap
// conversational search is not enabled, and normal conversational search is enabled, then there needs to be a citations
// title value. Lastly, if normal conversational search is not enabled, then there does not need to be a citations title
// for the config to be valid.
const selectIsConversationalSearchConfigValid = (state: RootState) => {
  return (
    selectIsSapConversationalSearchEnabled(state) ||
    (selectDraftIsConversationalSearchEnabled(state)
      ? !!selectDraftCitationsTitleValue(state)
      : true)
  );
};

// Will be set to false if there are any validation errors when configuring the elastic config or if any of the values
// are missing from the config. There is no check for url, filter, or query body fields because those fields are
// optional. Lastly, check that the conversational search config is valid.
export const selectIsConfigValid = (state: RootState) => {
  return (
    !!selectIsElasticConfigValid(state) &&
    !!selectDraftTitleOptionValue(state) &&
    !!selectDraftBodyOptionValue(state) &&
    !!selectIsConversationalSearchConfigValid(state)
  );
};
